import { KARAOKE_TRACK_NUMBER } from '../stores/VideoCreatorStore';
import { deepClone } from '../utility/deepClone';
import {
  adjustTrackNumbersToStartFromOne,
  applyDeletionToTranscript,
  applyShiftingToTranscript,
  getClosestElementIndexToLeftByFilter,
  getClosestElementIndexToRightByFilter,
  getClosestNotRemovedElementIndexToLeft,
  getClosestNotRemovedTextIndexToLeft,
  getClosestNotRemovedTextIndexToRight,
  getClosestTextIndexToLeft,
  getClosestTextIndexToRight,
} from './utils';

import type { ElementState } from '../renderer/ElementState';
import type { Renderer } from '../renderer/Renderer';
import type VideoCreatorStore from '../stores/VideoCreatorStore';
import type {
  TranscriptChange,
  TranscriptElement,
  TranscriptTextElement,
} from '../types.ts/transcript';
import type TranscriptionProcessor from './TranscriptionProcessor';

export default class VideoToTranscriptionProcessor {
  private videoCreator: VideoCreatorStore;
  private renderer?: Renderer;
  private transcriptionProcessor: TranscriptionProcessor;

  constructor(
    videoCreator: VideoCreatorStore,
    transcriptionProcessor: TranscriptionProcessor,
  ) {
    this.videoCreator = videoCreator;
    this.transcriptionProcessor = transcriptionProcessor;
  }

  get videoSourceStore() {
    return this.videoCreator.rootStore.videoSourceStore;
  }

  setRenderer(renderer: Renderer) {
    this.renderer = renderer;
  }

  /*
    Operations on video tracks reflected in transcription
  */
  async trimTrackStart(
    elementId: number,
    newStartTime: number,
    trimStart: number,
    newDuration: number,
    editSource: boolean = true, // Make changes to source or not.  For headless transition.
  ) {
    let returnRanges;
    const source = this.videoSourceStore.source;

    if (!source) {
      return;
    }

    const elementToTrim = source.elements.find(
      (el: Record<string, any>) => el.id === elementId,
    );

    if (!elementToTrim) {
      return;
    }

    const currentTrimStart = elementToTrim.trim_start || 0;

    if (elementToTrim.type === 'video') {
      if (currentTrimStart < trimStart) {
        const newChange = applyDeletionToTranscript(
          elementToTrim.time,
          elementToTrim.time + trimStart - currentTrimStart,
          true,
          this.transcriptionProcessor.getFinalTranscriptionElements(),
        );
        if (newChange) {
          newChange.command = `trimTrackStart(${elementId}, ${newStartTime}, ${trimStart}, ${newDuration})`;
          this.transcriptionProcessor.applyChange(newChange);
        }
      } else {
        returnRanges = this.untrimTextElements(
          trimStart,
          currentTrimStart,
          newStartTime,
          'start',
        );
      }
    }

    // Trim the element
    if (editSource) {
      await this.videoSourceStore.applyModifications({
        [`${elementId}.time`]: newStartTime,
        [`${elementId}.trim_start`]: trimStart,
        [`${elementId}.duration`]: newDuration,
      });
    }
    return returnRanges;
  }

  async moveElements(
    elementIds: string[],
    time: number,
    editSource: boolean = true,
  ) {
    console.log('move active tracks by', time);
    // const state = this.renderer!.state;
    // if (!state) {
    //   return;
    // }

    const source = this.videoSourceStore.source;
    if (!source) {
      return;
    }

    const selectedOriginalVideoElements = source.elements
      .filter(
        (el: Record<string, any>) =>
          elementIds.includes(el.id) &&
          this.videoCreator.isOriginalVideoElement(el),
      )
      .sort((el: Record<string, any>) => el.globalTime);

    if (selectedOriginalVideoElements.length > 0) {
      const fromTs = selectedOriginalVideoElements.at(0)!.time;
      const toTs =
        selectedOriginalVideoElements.at(-1)!.time +
        selectedOriginalVideoElements.at(-1)!.duration;

      const newChange = applyShiftingToTranscript(
        fromTs,
        toTs,
        fromTs + time,
        this.transcriptionProcessor.getFinalTranscriptionElements(),
      ) as TranscriptChange;
      newChange.command = `moveElements(${elementIds}, ${time})`;
      this.transcriptionProcessor.applyChange(newChange);
      this.videoCreator.subtitlesProcessor.moveSubtitles(fromTs, toTs, time);
    }

    if (!editSource) {
      return;
    }

    const modificationObject: any = {};
    for (const element of source.elements) {
      if (elementIds.includes(element.id)) {
        modificationObject[`${element.id}.time`] = element.time + time;
      }
    }

    await this.videoSourceStore.applyModifications(modificationObject);
  }

  async moveTrack(
    elementId: number,
    newStartTime: number,
    editSource: boolean = true,
  ) {
    // const state = deepClone(this.renderer!.state);
    // if (!state) {
    //   return;
    // }

    const source = this.videoSourceStore.source;
    if (!source) {
      return;
    }

    const elementToMove = source.elements.find(
      (el: Record<string, any>) => el.id === elementId,
    );

    if (!elementToMove) {
      return;
    }

    //// debugger;
    if (elementToMove.type === 'video') {
      const newChange = applyShiftingToTranscript(
        elementToMove.time,
        elementToMove.time + elementToMove.duration,
        newStartTime,
        this.transcriptionProcessor.getFinalTranscriptionElements(),
      );
      if (newChange) {
        newChange.command = `moveTrack(${elementId}, ${newStartTime})`;
        this.transcriptionProcessor.applyChange(newChange);
      }
      this.videoCreator.subtitlesProcessor.moveSubtitles(
        elementToMove.time,
        elementToMove.time + elementToMove.duration,
        newStartTime - elementToMove.time,
      );
    }

    if (editSource) {
      await this.videoSourceStore.applyModifications({
        [`${elementId}.time`]: newStartTime,
      });
    }
  }

  async trimTrackDuration(
    elementId: string,
    duration: number,
    editSource: boolean = true, // Make changes to source or not.  For headless transition.
  ) {
    let newChange: TranscriptChange;
    let returnRanges;
    // Logic to trim a track from the end
    // const state = deepClone(this.renderer!.state);
    // if (!state) {
    //   return;
    // }

    const source = this.videoSourceStore.source;
    if (!source) {
      return;
    }

    const elementToTrim = source.elements.find(
      (el: Record<string, any>) => el.id === elementId,
    );

    if (!elementToTrim || elementToTrim.type !== 'video') {
      return;
    }
    const trimStart = elementToTrim.trim_start || 0;

    if (elementToTrim.duration > duration) {
      const deletionChange = applyDeletionToTranscript(
        elementToTrim.time + duration,
        elementToTrim.time + elementToTrim.duration,
        true,
        this.transcriptionProcessor.getFinalTranscriptionElements(),
      );
      if (deletionChange) {
        newChange = deletionChange;
        newChange.command = `trimTrackDuration(${elementId}, ${duration})`;
        this.transcriptionProcessor.applyChange(newChange);
      }
    } else {
      returnRanges = this.untrimTextElements(
        trimStart + elementToTrim.duration,
        trimStart + duration,
        elementToTrim.time + elementToTrim.duration,
        'end',
      );
    }
    // }

    if (editSource) {
      await this.videoSourceStore.applyModifications({
        [`${elementId}.duration`]: duration,
      });
    }

    return returnRanges;
    // Set source by the mutated state
    // await this.renderer!.setSource(this.renderer!.getSource(state), true);
  }

  /**
   * Removes a track from the video and transcription
   * @param elementId
   */
  async deleteOriginalVideoTrack(
    elementId: string,
    editSource: boolean = true,
  ) {
    // Logic to remove a track from both video and transcription
    let initialSource = deepClone(this.videoSourceStore.source);
    if (!initialSource) {
      return;
    }

    const elementToDelete = initialSource.elements.find(
      (el: Record<string, any>) => el.id === elementId,
    );
    if (elementToDelete?.type === 'video') {
      const deletionChange = applyDeletionToTranscript(
        elementToDelete.time,
        elementToDelete.time + elementToDelete.duration,
        false,
        this.transcriptionProcessor.getFinalTranscriptionElements(),
      );

      if (editSource) {
        // shift tracks to the left after this one
        this.videoSourceStore.source.elements.forEach(
          (element: Record<string, any>) => {
            if (
              element.time >=
              elementToDelete.time + elementToDelete.duration
            ) {
              element.time = element.time - elementToDelete.duration;
            }
          },
        );
      }

      if (deletionChange) {
        deletionChange.command = `deleteOriginalVideoTrack(${elementId})`;
        this.transcriptionProcessor.applyChange(deletionChange);
      } else {
        const shiftChange = applyShiftingToTranscript(
          elementToDelete.time + elementToDelete.duration,
          initialSource.duration,
          elementToDelete.time,
          this.transcriptionProcessor.getFinalTranscriptionElements(),
        ) as TranscriptChange;
        if (shiftChange) {
          shiftChange.command = `deleteOriginalVideoTrack(${elementId})`;
          this.transcriptionProcessor.applyChange(shiftChange);
        }
      }
    }

    if (editSource) {
      const filteredSource = {
        ...this.videoSourceStore.source,
        elements: this.videoSourceStore.source.elements.filter(
          (el: Record<string, any>) => el.id !== elementId,
        ),
      };

      // Set source by the mutated state
      const updatedSource = adjustTrackNumbersToStartFromOne(filteredSource);
      this.videoSourceStore.setSource(updatedSource, true);

      // TODO: move to creatorStore where deleteOriginalVideoTrack is called
      this.videoCreator.trackManager.updateFrameLockedTracksAfterRearrange(
        initialSource.elements,
        this.videoSourceStore.source.elements,
      );
    }
  }

  async untrimTextElements(
    fromTs: number,
    toTs: number,
    newTs: number,
    type: 'start' | 'end',
  ) {
    const rangesToUntrim =
      type === 'start'
        ? this.getRangesToUntrimStart(fromTs, toTs, newTs)
        : this.getRangesToUntrimEnd(fromTs, toTs, newTs);
    const elementsCount =
      rangesToUntrim.lastIndexToUntrim - rangesToUntrim.firstIndexToUntrim;
    this.transcriptionProcessor.applyChange({
      index: -1,
      count: 0,
      type: type === 'start' ? 'untrim_start' : 'untrim_end',
      fromTs,
      toTs,
      newTs,
      datetime: new Date().toISOString(),
      command: `untrimTextElements(${fromTs}, ${toTs}, ${newTs}, ${type})`,
    });

    if ('firstTextElementIndex' in rangesToUntrim) {
      let fromIndex =
        getClosestTextIndexToLeft(
          rangesToUntrim.firstTextElementIndex - 1,
          this.transcriptionProcessor.getFinalTranscriptionElements(),
        ) - elementsCount;
      fromIndex = fromIndex >= 0 ? fromIndex : 0;
      return {
        fromIndex,
        toIndex: rangesToUntrim.firstTextElementIndex,
      };
    } else {
      let toIndex = getClosestTextIndexToRight(
        rangesToUntrim.lastTextElementIndex + 1,
        this.transcriptionProcessor.getFinalTranscriptionElements(),
      );
      toIndex =
        toIndex >= 0
          ? toIndex + elementsCount
          : this.transcriptionProcessor.getFinalTranscriptionElements().length;
      return {
        fromIndex: rangesToUntrim.lastTextElementIndex,
        toIndex,
      };
    }
  }

  async applyKaraokeElementPlacement(
    element: ElementState,
    placement: Pick<ElementState, 'duration' | 'globalTime' | 'trimStart'>,
    isSubtitles: boolean = false,
  ) {
    if (
      element.globalTime === placement.globalTime &&
      element.duration === placement.duration
    )
      return;

    const transcriptElements = isSubtitles
      ? this.videoCreator.subtitleElements!
      : this.transcriptionProcessor.getFinalTranscriptionElements();

    // NEW PLANM
    // Find karaoke block via element id matching.
    // x.findIndex( e => e.source.id == element.source.id )
    // Then go to transcript, get all transcript elements from that block (discard everyting before break index)
    // Use those transcript elements to adjust timing.

    // Get which karaoke break we're on.
    // Pre-filter karaoke elements to avoid repeated filter calls
    const karaokeElements =
      this.videoSourceStore.source.elements.filter(
        (e: Record<string, any>) => e.track == KARAOKE_TRACK_NUMBER,
      ) || [];

    // Determine the index of the current karaoke block
    const karaokeBlockIndex = karaokeElements.findIndex(
      (el: Record<string, any>) => el.id === element.source.id,
    );

    // Validate if a karaoke block was found
    if (karaokeBlockIndex === -1) {
      console.error('No karaoke block found');
      return;
    }

    // Check if the current block is the last one
    const isLastKaraokeBlock = karaokeBlockIndex === karaokeElements.length - 1;

    // Variables to track the start and end of the current karaoke block
    let karaokeBreakCount = 0;
    let breakStartIndex, breakEndIndex;

    // Special handling for the first karaoke block in the sequence
    if (karaokeBlockIndex === 0) {
      // Find first active element, assume a 'break' right before.
      breakStartIndex =
        getClosestNotRemovedTextIndexToRight(0, transcriptElements) - 1;
      breakEndIndex = 0;

      // Find the end of the block by locating the first karaoke break, or reach the end
      while (
        breakEndIndex < transcriptElements.length &&
        !transcriptElements[breakEndIndex]?.karaoke_break
      ) {
        breakEndIndex++;
      }

      // Adjust if no trailing karaoke break is found
      if (!transcriptElements[breakEndIndex]?.karaoke_break) {
        breakEndIndex = transcriptElements.length;
      }
    } else {
      // Iterate over transcript elements to find the nth karaoke block
      for (let i = 0; i < transcriptElements.length; i++) {
        if (transcriptElements[i].karaoke_break) {
          karaokeBreakCount++;
          // Check if current element is the start of the desired karaoke block
          if (karaokeBreakCount === karaokeBlockIndex) {
            breakStartIndex = i;
            breakEndIndex = i + 1;

            // Continue to find the next karaoke break to mark end of this block
            while (
              breakEndIndex < transcriptElements.length &&
              !transcriptElements[breakEndIndex]?.karaoke_break
            ) {
              breakEndIndex++;
            }

            // If it's the last block, adjust the end index to the very end if needed
            if (
              isLastKaraokeBlock &&
              !transcriptElements[breakEndIndex]?.karaoke_break
            ) {
              breakEndIndex = transcriptElements.length;
            }

            break; // Exit loop once the current block is processed
          }
        }
      }
    }

    // Need to check indices explicitly since 0 is a valid index
    if (breakStartIndex == null || breakEndIndex == null) {
      console.error('No karaoke break element found');
      return;
    }

    console.log('karaoke block', breakStartIndex, breakEndIndex);

    // Grab transcriptElement timing immediately after/before our start/end karaoke breaks.
    const startTranscriptElementIndex = getClosestElementIndexToRightByFilter(
      // @ts-ignore
      breakStartIndex + 1, //breakStartIndex points to karaoke break of previous line
      transcriptElements,
      (el: TranscriptElement) =>
        el.state !== 'removed' &&
        el.state !== 'cut' &&
        !!el.value &&
        el.ts != null,
    );
    const startTranscriptElement =
      transcriptElements[startTranscriptElementIndex];
    const endTranscriptElementIndex = getClosestElementIndexToLeftByFilter(
      breakEndIndex,
      transcriptElements,
      (el: TranscriptElement) =>
        el.state !== 'removed' &&
        el.state !== 'cut' &&
        !!el.value &&
        el.ts != null,
    );
    const endTranscriptElement = transcriptElements[endTranscriptElementIndex];

    let changes: Array<
      TranscriptChange & {
        type:
          | 'change_karaoke_start_break_time'
          | 'change_karaoke_end_break_time';
      }
    > = [];

    const updatedKaraokeBreakStartTsDiff =
      placement.globalTime - startTranscriptElement.ts!;

    const updatedKaraokeBreakEndTsDiff =
      placement.globalTime + placement.duration - endTranscriptElement.end_ts!;

    const timestamp = new Date().toISOString();

    if (isSubtitles) {
      this.videoCreator.subtitlesProcessor.applyPlacementToElement(
        startTranscriptElementIndex,
        {
          karaoke_break_start_ts_diff: updatedKaraokeBreakStartTsDiff,
        },
      );

      this.videoCreator.subtitlesProcessor.applyPlacementToElement(
        endTranscriptElementIndex,
        {
          karaoke_break_end_ts_diff: updatedKaraokeBreakEndTsDiff,
        },
      );
    } else {
      changes.push(
        {
          type: 'change_karaoke_start_break_time',
          index: startTranscriptElementIndex,
          timeShift: updatedKaraokeBreakStartTsDiff,
          count: 1,
          datetime: timestamp,
          version: 2,
        },
        {
          type: 'change_karaoke_end_break_time',
          index: endTranscriptElementIndex,
          timeShift: updatedKaraokeBreakEndTsDiff,
          count: 1,
          datetime: timestamp,
          version: 2,
        },
      );

      for (let change of changes) {
        this.transcriptionProcessor.applyChange(change);
      }
    }

    await this.videoSourceStore.applyModifications({
      [`${element.source.id}.time`]: placement.globalTime,
      [`${element.source.id}.duration`]: placement.duration,
    });
  }

  async applyKaraokeElementPlacementOld(
    element: ElementState,
    placement: Pick<ElementState, 'duration' | 'globalTime' | 'trimStart'>,
    isSubtitles: boolean = false,
  ) {
    if (
      element.globalTime === placement.globalTime &&
      element.duration === placement.duration
    )
      return;

    const transcriptElements = isSubtitles
      ? this.videoCreator.subtitleElements!
      : this.transcriptionProcessor.getFinalTranscriptionElements();
    const lastTranscriptElementIndex = getClosestNotRemovedElementIndexToLeft(
      transcriptElements.length - 1,
      transcriptElements,
    );

    const endTranscriptElement = transcriptElements.findIndex(
      (el, index, arr) => {
        if (!el.karaoke_break && index !== lastTranscriptElementIndex)
          return false;
        const lastTextElementIndex = getClosestNotRemovedTextIndexToLeft(
          index,
          arr,
        );
        if (lastTextElementIndex < 0) return false;
        const lastTextEl = arr[lastTextElementIndex] as TranscriptTextElement;

        if (lastTextEl.karaoke_break_end_ts_diff) {
          return (
            Math.abs(
              lastTextEl.end_ts +
                lastTextEl.karaoke_break_end_ts_diff -
                (element.globalTime + element.duration),
            ) < 0.001
          );
        }
        if (lastTextElementIndex > -1) {
          return (
            Math.abs(
              lastTextEl.end_ts - (element.globalTime + element.duration),
            ) < 0.001
          );
        }

        return false;
      },
    );
    if (endTranscriptElement < 0) {
      console.error('No end karaoke break element found');
      return;
    }

    const startTranscriptElement = getClosestElementIndexToLeftByFilter(
      endTranscriptElement,
      transcriptElements,
      (el: TranscriptElement, index: number) => {
        const prev = getClosestNotRemovedElementIndexToLeft(
          index - 1,
          transcriptElements,
          true,
          true,
        );
        if (prev < 0) return true;
        const prevElement = transcriptElements[prev];
        if (prevElement.karaoke_break) return true;
        return false;
      },
    );
    if (startTranscriptElement < 0) {
      console.error('No start karaoke break element found');
      return;
    }

    const closestTextElementStartIndex = getClosestNotRemovedTextIndexToRight(
      startTranscriptElement,
      transcriptElements,
    );
    const closestTextElementStart = transcriptElements[
      closestTextElementStartIndex
    ] as TranscriptTextElement;
    const closestTextElementEndIndex = getClosestNotRemovedTextIndexToLeft(
      endTranscriptElement,
      transcriptElements,
    );
    const closestTextElementEnd = transcriptElements[
      closestTextElementEndIndex
    ] as TranscriptTextElement;

    let changes: Array<
      TranscriptChange & {
        type:
          | 'change_karaoke_start_break_time'
          | 'change_karaoke_end_break_time';
      }
    > = [];

    if (element.globalTime !== placement.globalTime) {
      if (isSubtitles) {
        this.videoCreator.subtitlesProcessor.applyPlacementToElement(
          closestTextElementStartIndex,
          {
            karaoke_break_start_ts_diff:
              placement.globalTime - closestTextElementStart.ts!,
          },
        );
      }
      changes.push({
        type: 'change_karaoke_start_break_time',
        index: closestTextElementStartIndex,
        timeShift: placement.globalTime - closestTextElementStart.ts!,
        count: 1,
        datetime: new Date().toISOString(),
        version: 2,
      });
    }
    if (
      Math.abs(
        element.globalTime +
          element.duration -
          placement.globalTime -
          placement.duration,
      ) > 0.01
    ) {
      if (isSubtitles) {
        this.videoCreator.subtitlesProcessor.applyPlacementToElement(
          closestTextElementEndIndex,
          {
            karaoke_break_end_ts_diff:
              placement.globalTime +
              placement.duration -
              closestTextElementEnd.end_ts!,
          },
        );
      }
      changes.push({
        type: 'change_karaoke_end_break_time',
        index: closestTextElementEndIndex,
        timeShift:
          placement.globalTime +
          placement.duration -
          closestTextElementEnd.end_ts!,
        count: 1,
        datetime: new Date().toISOString(),
        version: 2,
      });
    }
    if (changes.length === 0) return;
    if (!isSubtitles) {
      for (let change of changes) {
        this.transcriptionProcessor.applyChange(change);
      }
    }
    await this.videoSourceStore.applyModifications({
      [`${element.source.id}.time`]: placement.globalTime,
      [`${element.source.id}.duration`]: placement.duration,
    });
  }

  private getRangesToUntrimStart(fromTs: number, toTs: number, intoTs: number) {
    const originalElements =
      this.transcriptionProcessor.getTranscriptionElements();
    let finalElements =
      this.transcriptionProcessor.getFinalTranscriptionElements();
    const addedDuration = toTs - fromTs;

    let firstTextElementIndex = finalElements.findIndex(
      el =>
        el.type === 'text' &&
        el.state !== 'removed' &&
        el.state !== 'cut' &&
        el.end_ts > intoTs + addedDuration,
    );
    // debugger;
    const firstIndexToUntrim = originalElements.findIndex(
      el =>
        el.type === 'text' &&
        el.end_ts! > fromTs &&
        el.end_ts! - fromTs > fromTs - el.ts!,
    );

    let lastIndexToUntrim = originalElements.findIndex(
      el => el.type === 'text' && el.end_ts! > toTs,
    );
    return { firstIndexToUntrim, lastIndexToUntrim, firstTextElementIndex };
  }

  private getRangesToUntrimEnd(fromTs: number, toTs: number, intoTs: number) {
    const originalElements =
      this.transcriptionProcessor.getTranscriptionElements();
    let finalElements =
      this.transcriptionProcessor.getFinalTranscriptionElements();
    //@ts-ignore
    let lastTextElementIndex = finalElements.findLastIndex(
      //@ts-ignore
      el =>
        el.type === 'text' &&
        el.state !== 'removed' &&
        el.state !== 'cut' &&
        el.ts < intoTs,
    );
    // debugger;
    //@ts-ignore
    const lastIndexToUntrim = originalElements.findLastIndex(
      //@ts-ignore
      el =>
        el.type === 'text' &&
        el.ts! < toTs &&
        el.end_ts! - toTs < toTs - el.ts!,
    );

    //@ts-ignore
    let firstIndexToUntrim = originalElements.findLastIndex(
      //@ts-ignore
      el => el.type === 'text' && el.ts < fromTs,
    );

    return { firstIndexToUntrim, lastIndexToUntrim, lastTextElementIndex };
  }
}
